import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';

interface QmsRecordActionsDropdownProps {
  recordId: string;
  basePath: string;
  canManage: boolean;
}

/**
 * Menú de acciones al estilo encuestas/evaluaciones (tres puntos + portal).
 */
export function QmsRecordActionsDropdown({ recordId, basePath, canManage }: QmsRecordActionsDropdownProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, openUp: false });
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleScrollResize = () => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 200;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUp = spaceBelow < dropdownHeight;
        setCoords({
          top: openUp ? rect.top + window.scrollY - dropdownHeight - 8 : rect.bottom + window.scrollY,
          left: rect.right - 224 + window.scrollX,
          openUp,
        });
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      window.addEventListener('scroll', handleScrollResize);
      window.addEventListener('resize', handleScrollResize);
      handleScrollResize();
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleScrollResize);
      window.removeEventListener('resize', handleScrollResize);
    };
  }, [isOpen]);

  const go = (path: string) => {
    navigate(path);
    setIsOpen(false);
  };

  const menu = (
    <div
      ref={dropdownRef}
      style={{ position: 'absolute', top: `${coords.top}px`, left: `${coords.left}px`, zIndex: 9999 }}
      className={`w-56 rounded-md shadow-lg bg-card border border-border ring-1 ring-black/5 overflow-hidden animate-in fade-in zoom-in-95 duration-100 ${
        coords.openUp ? 'origin-bottom' : 'origin-top'
      }`}
    >
      <div className="py-1" role="menu">
        <button
          type="button"
          onClick={() => go(`${basePath}/${recordId}`)}
          className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
          role="menuitem"
        >
          <Icon icon="mdi:file-document-outline" className="w-4 h-4 mr-3 text-muted-foreground" />
          Ver detalle completo
        </button>
        <button
          type="button"
          onClick={() => go(`${basePath}/${recordId}/planes`)}
          className="flex items-center w-full px-4 py-2 text-sm text-foreground hover:bg-muted transition-colors text-left"
          role="menuitem"
        >
          <Icon icon="mdi:clipboard-list-outline" className="w-4 h-4 mr-3 text-muted-foreground" />
          {canManage ? 'Planes de acción' : 'Mis acciones asignadas'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="relative flex justify-end">
      <button
        ref={buttonRef}
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="inline-flex items-center justify-center rounded-md p-2 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors focus:outline-none focus:ring-2 focus:ring-primary"
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label="Acciones"
      >
        <Icon icon="mdi:dots-vertical" className="w-5 h-5" />
      </button>
      {isOpen && createPortal(menu, document.body)}
    </div>
  );
}
